import { useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import { Button } from '@rudi/ui/button';
import { PageHeader } from '../components/page';
import {
  usePlatformVoiceWritingOptions,
  usePlatformVoiceWritingPolicy,
  useSavePlatformVoiceWritingPolicy,
} from '../api/hooks';
import type {
  VoiceWritingOptions,
  VoiceWritingPolicy,
  VoiceWritingPolicyRead,
  VoiceWritingRoute,
  VoiceWritingRoutePlan,
} from '../api/types';
import {
  OPENROUTER_EU_TRANSCRIPTION_ENDPOINT,
  supportsConsultationSpeech,
} from './TenantVoiceWritingSection';

type Route = { provider: string; model: string };
type Choice = Route & { configured: boolean; route?: VoiceWritingRoute };
type Settings = { routes: Route[]; revision: number };
const names: Record<string, string> = {
  groq: 'Groq',
  infercom: 'Infercom',
  deepinfra: 'DeepInfra',
  openrouter: 'OpenRouter',
};
const catalog = [
  {
    provider: 'groq',
    model: 'whisper-large-v3',
    endpoint: 'https://api.groq.com/openai/v1/audio/transcriptions',
    region: 'us',
  },
  {
    provider: 'groq',
    model: 'whisper-large-v3-turbo',
    endpoint: 'https://api.groq.com/openai/v1/audio/transcriptions',
    region: 'us',
  },
  {
    provider: 'infercom',
    model: 'Whisper-Large-v3',
    endpoint: 'https://api.infercom.ai/v1/audio/transcriptions',
    region: 'eu',
  },
  {
    provider: 'deepinfra',
    model: 'openai/whisper-large-v3-turbo',
    endpoint: 'https://api.deepinfra.com/v1/openai/audio/transcriptions',
    region: 'us',
  },
  // Mistral's models run only on OpenRouter's EU domain; Rudi refuses them elsewhere.
  ...[
    'mistralai/voxtral-small-transcribe-3-0',
    'mistralai/voxtral-mini-transcribe-3-0',
    'mistralai/voxtral-mini-transcribe',
  ].map(model => ({
    provider: 'openrouter',
    model,
    endpoint: OPENROUTER_EU_TRANSCRIPTION_ENDPOINT,
    region: 'eu',
  })),
  // OpenRouter's EU domain serves no endpoint for these.
  ...['openai/whisper-large-v3', 'elevenlabs/scribe-v2-medical'].map(model => ({
    provider: 'openrouter',
    model,
    endpoint: 'https://openrouter.ai/api/v1/audio/transcriptions',
    region: 'global',
  })),
];
const id = (route: Route) => `${route.provider}/${route.model}`;
const control = 'mt-1 w-full rounded-3xl border border-input bg-background px-3 py-2 text-sm';

export function rudiSpeechChoices(options: VoiceWritingOptions): Choice[] {
  return catalog.map(item => {
    const protocol =
      item.provider === 'openrouter' ? 'openrouter_transcription' : 'openai_transcription';
    const credential_ref = `platform/${item.provider}`;
    const approved = options.approvals.some(
      approval =>
        approval.credential_ref === credential_ref &&
        approval.endpoints.includes(item.endpoint) &&
        approval.regions.includes(item.region)
    );
    const installed = options.models.some(
      model => model.protocol === protocol && model.model === item.model && model.installed
    );
    const credential = options.credentials?.find(value => value.credential_ref === credential_ref);
    return {
      ...item,
      configured: approved && installed && !!credential?.source,
      route: {
        protocol,
        model: item.model,
        effort: null,
        endpoint: item.endpoint,
        credential_ref,
        region: item.region,
      },
    };
  });
}

export function SpeechForm({
  saved,
  choices,
  pending,
  onSave,
  label = 'Speech recognition settings',
}: {
  saved: Settings;
  choices: Choice[];
  pending: boolean;
  onSave: (routes: Route[], revision: number) => Promise<void>;
  label?: string;
}) {
  const [routes, setRoutes] = useState(saved.routes);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const valid =
    routes.length > 0 &&
    new Set(routes.map(id)).size === routes.length &&
    routes.every(route => choices.some(choice => id(choice) === id(route) && choice.configured));
  return (
    <form
      aria-label={label}
      className="space-y-4"
      onSubmit={event => {
        event.preventDefault();
        setError(null);
        setMessage(null);
        void onSave(routes, saved.revision)
          .then(() => setMessage('Speech settings saved. Changes apply to the next recording.'))
          .catch(() => setError('Could not save speech settings. Reload and retry.'));
      }}
    >
      <fieldset disabled={pending} className="space-y-4">
        {routes.map((route, index) => (
          <div key={index} className="flex items-end gap-2">
            <label className="min-w-0 flex-1 text-sm">
              {index === 0 ? 'Primary model' : `Backup ${index}`}
              <select
                className={control}
                value={id(route)}
                onChange={event => {
                  const choice = choices.find(item => id(item) === event.target.value);
                  if (choice)
                    setRoutes(
                      routes.map((value, position) =>
                        position === index
                          ? { provider: choice.provider, model: choice.model }
                          : value
                      )
                    );
                }}
              >
                {!choices.some(choice => id(choice) === id(route)) && (
                  <option value={id(route)}>{route.model || 'Choose a model'}</option>
                )}
                {choices.map(choice => (
                  <option
                    key={id(choice)}
                    value={id(choice)}
                    disabled={
                      !choice.configured ||
                      routes.some(
                        (value, position) => position !== index && id(value) === id(choice)
                      )
                    }
                  >
                    {names[choice.provider] ?? choice.provider} · {choice.model}
                    {choice.route ? ` · ${choice.route.region}` : ''}
                    {choice.configured ? '' : ' · unavailable'}
                  </option>
                ))}
              </select>
            </label>
            {index > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setRoutes(routes.filter((_, position) => position !== index))}
              >
                Remove backup {index}
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={
            routes.length >= 4 ||
            !choices.some(
              choice => choice.configured && !routes.some(route => id(route) === id(choice))
            )
          }
          onClick={() => {
            const choice = choices.find(
              choice => choice.configured && !routes.some(route => id(route) === id(choice))
            );
            if (choice) setRoutes([...routes, { provider: choice.provider, model: choice.model }]);
          }}
        >
          {routes.length ? 'Add backup' : 'Choose primary model'}
        </Button>
        <p className="text-sm text-muted-foreground">
          Unavailable models need a deployed provider key and an approved speech route. OpenBao
          supplies deployment keys separately for each application and environment. A configured key
          does not confirm access or credit.
        </p>
        <p className="text-sm text-muted-foreground">
          Backups run for missing keys, connection failures, rate limits and provider errors.
          Silence and rejected audio do not trigger a backup. OpenRouter chooses its upstream
          provider; only its EU endpoint, which Mistral models require, keeps processing in the EU.
        </p>
        <Button type="submit" disabled={!valid || pending}>
          {pending ? 'Saving...' : 'Save speech settings'}
        </Button>
      </fieldset>
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
    </form>
  );
}

function planRoutes(plan: VoiceWritingRoutePlan | null | undefined): Route[] {
  return plan
    ? [plan.primary, ...plan.fallbacks].map(route => ({
        provider: route.credential_ref.split('/').at(-1) ?? '',
        model: route.model,
      }))
    : [];
}

function describePlan(plan: VoiceWritingRoutePlan) {
  return [plan.primary, ...plan.fallbacks].map(route => route.model).join(', then ');
}

// What video consultations run without their own override: Rudi resolves
// consultation_stt as its override, then stt_default, then the dictation override.
function consultationFallback(policy: VoiceWritingPolicy) {
  if (policy.stt_default)
    return `Uses the speech recognition default: ${describePlan(policy.stt_default)}.`;
  if (policy.overrides.dictation_stt)
    return `Uses the dictation setting: ${describePlan(policy.overrides.dictation_stt)}.`;
  return 'No speech model is set, so video consultations have no live captions or transcripts until you choose one.';
}

function RudiSpeech() {
  const policy = usePlatformVoiceWritingPolicy();
  const options = usePlatformVoiceWritingOptions();
  const save = useSavePlatformVoiceWritingPolicy();
  const [audioDays, setAudioDays] = useState<number | null>(null);
  const [textDays, setTextDays] = useState<number | null>(null);
  const [clearError, setClearError] = useState<string | null>(null);
  if (policy.isLoading || options.isLoading)
    return <p role="status">Loading Rudi speech settings...</p>;
  if (policy.error || options.error || !policy.data || !options.data || !policy.data.editable)
    return <p role="alert">Rudi speech settings are unavailable for this target.</p>;
  const read: VoiceWritingPolicyRead = policy.data;
  const choices = rudiSpeechChoices(options.data);
  const consultations = supportsConsultationSpeech(read);
  const consultationOverride = read.policy?.overrides.consultation_stt;

  // Saves one purpose's override and preserves everything else in the policy:
  // the other purpose, meeting routes, retention and writing settings.
  const savePurpose = async (
    revision: number,
    edit: (next: VoiceWritingPolicy) => VoiceWritingRoute[]
  ) => {
    const next = structuredClone(read.policy) as VoiceWritingPolicy | null;
    if (!next) throw new Error('Rudi has no editable policy');
    const selected = edit(next);
    // Rudi refuses a route whose region the policy does not approve.
    next.approved_regions = [
      ...new Set([...next.approved_regions, ...selected.map(route => route.region)]),
    ];
    await save.mutateAsync({ expected_revision: revision, policy: next });
  };
  const toPlan = (routes: Route[]): VoiceWritingRoutePlan => {
    const selected = routes.map(route => choices.find(choice => id(choice) === id(route))?.route);
    if (selected.some(route => !route) || !selected[0]) throw new Error('Unsupported speech route');
    return { primary: selected[0], fallbacks: selected.slice(1) as VoiceWritingRoute[] };
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        This changes Rudi's platform default. Tenants with their own policy keep their override.
        Chat keeps its own selection. Saving adds each chosen model's region to the approved
        regions.
      </p>
      <section aria-labelledby="rudi-dictation-speech" className="space-y-4">
        <h2 id="rudi-dictation-speech" className="text-lg font-semibold">
          Dictation
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm">
            Dictation audio retention in days
            <input
              type="number"
              min={0}
              max={36500}
              required
              className={control}
              value={audioDays ?? read.policy?.retention.dictation_audio ?? ''}
              onChange={event =>
                setAudioDays(event.target.value === '' ? null : Number(event.target.value))
              }
            />
          </label>
          <label className="text-sm">
            Dictation text retention in days
            <input
              type="number"
              min={0}
              max={36500}
              required
              className={control}
              value={textDays ?? read.policy?.retention.dictation_text ?? ''}
              onChange={event =>
                setTextDays(event.target.value === '' ? null : Number(event.target.value))
              }
            />
          </label>
        </div>
        <p className="text-sm text-muted-foreground">
          Set retention before saving. Zero deletes the retained content when processing finishes.
        </p>
        <SpeechForm
          key={read.revision}
          label="Dictation speech settings"
          saved={{
            routes: planRoutes(read.policy?.overrides.dictation_stt ?? read.policy?.stt_default),
            revision: read.revision,
          }}
          choices={choices}
          pending={save.isPending}
          onSave={(routes, revision) =>
            savePurpose(revision, next => {
              const plan = toPlan(routes);
              next.retention.dictation_audio = audioDays ?? next.retention.dictation_audio;
              next.retention.dictation_text = textDays ?? next.retention.dictation_text;
              if (next.retention.dictation_audio === null || next.retention.dictation_text === null)
                throw new Error('Set dictation retention');
              next.overrides.dictation_stt = plan;
              return [plan.primary, ...plan.fallbacks];
            })
          }
        />
      </section>
      <section aria-labelledby="rudi-consultation-speech" className="space-y-4">
        <h2 id="rudi-consultation-speech" className="text-lg font-semibold">
          Video consultations
        </h2>
        {!consultations ? (
          <p role="note" className="text-sm text-muted-foreground">
            This Rudi release has one speech setting, for dictation. Update Rudi to choose a
            separate model for video consultations.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Live captions and consented consultation transcripts. Captions are not stored, and
              transcripts follow the consultation's own retention.
            </p>
            <div role="status" className="space-y-2 rounded-3xl border p-3 text-sm">
              <p>
                {consultationOverride
                  ? `Video consultations use their own setting. Without it: ${consultationFallback(read.policy!)}`
                  : consultationFallback(read.policy!)}
              </p>
              {consultationOverride && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={save.isPending}
                  onClick={() => {
                    setClearError(null);
                    savePurpose(read.revision, next => {
                      delete next.overrides.consultation_stt;
                      return [];
                    }).catch(() =>
                      setClearError(
                        'Could not clear the video consultation setting. Reload and retry.'
                      )
                    );
                  }}
                >
                  Clear video consultation setting
                </Button>
              )}
              {clearError && (
                <p role="alert" className="text-destructive">
                  {clearError}
                </p>
              )}
            </div>
            <SpeechForm
              key={read.revision}
              label="Video consultation speech settings"
              saved={{ routes: planRoutes(consultationOverride), revision: read.revision }}
              choices={choices}
              pending={save.isPending}
              onSave={(routes, revision) =>
                savePurpose(revision, next => {
                  const plan = toPlan(routes);
                  next.overrides.consultation_stt = plan;
                  return [plan.primary, ...plan.fallbacks];
                })
              }
            />
          </>
        )}
      </section>
    </div>
  );
}

function ChatSpeech() {
  const api = useApiClient();
  const qc = useQueryClient();
  const policy = useQuery({
    queryKey: ['chat-speech'],
    queryFn: async () => (await api.get<Settings>('/chat-model/speech')).data,
    retry: false,
  });
  const models = useQuery({
    queryKey: ['chat-speech-models'],
    queryFn: async () => (await api.get<{ models: Choice[] }>('/chat-model/speech/models')).data,
    retry: false,
  });
  const save = useMutation({
    mutationFn: async (settings: Settings) =>
      (await api.put<Settings>('/chat-model/speech', settings)).data,
    onSuccess: data => {
      qc.setQueryData(['chat-speech'], data);
    },
  });
  if (policy.isLoading || models.isLoading)
    return <p role="status">Loading Chat speech settings...</p>;
  if (policy.error || models.error || !policy.data || !models.data)
    return (
      <p role="alert">
        Chat speech settings are unavailable. Use the main connection and a Chat release that
        supports speech settings.
      </p>
    );
  return (
    <SpeechForm
      key={policy.data.revision}
      saved={policy.data}
      choices={models.data.models}
      pending={save.isPending}
      onSave={async (routes, revision) => {
        await save.mutateAsync({ routes, revision });
      }}
    />
  );
}

export function SpeechPage() {
  const navigate = useNavigate();
  const application = useRouterState({
    select: state =>
      new URLSearchParams(state.location.searchStr).get('application') === 'chat' ? 'chat' : 'rudi',
  });
  return (
    <div className="space-y-6">
      <PageHeader
        title="Speech recognition"
        description="Choose a speech model and backups separately for each application."
      />
      <label className="block max-w-sm text-sm">
        Application
        <select
          aria-label="Application"
          className={control}
          value={application}
          onChange={event =>
            void navigate({
              to: '/speech',
              search: { application: event.target.value === 'chat' ? 'chat' : 'rudi' },
            })
          }
        >
          <option value="rudi">Rudi</option>
          <option value="chat">Patient Chat</option>
        </select>
      </label>
      {application === 'rudi' ? <RudiSpeech /> : <ChatSpeech />}
    </div>
  );
}
